<template>
  <div class="video-srt-editor">
    <div v-if="!initialized" class="editor-waiting">
      <i class="el-icon-loading" style="font-size: 32px" />
      <p>页面加载中…</p>
    </div>
    <template v-else>
      <div class="editor-layout">
        <div class="video-section">
          <video
            ref="videoEl"
            class="video-player"
            :src="videoPath"
            controls
            @timeupdate="onVideoTimeUpdate"
          >
          </video>
          <div class="subtitle-preview">
            {{ currentCueText }}
          </div>
        </div>
        <div class="list-section">
          <div v-if="!readonly" class="list-toolbar">
            <span v-if="validationErrors.length" class="error-summary">
              {{ $tc('subtitle.validationIssueSummary', validationErrors.length, { count: validationErrors.length }) }}
            </span>
          </div>
          <div ref="subtitleListEl" class="subtitle-list">
            <div
              v-for="(item, i) in list"
              :key="item.id"
              :class="['subtitle-item', { active: currentIndex === i, error: !readonly && getItemErrors(item).length }]"
              @click="onSubtitleClick(item, i)"
            >
              <div class="subtitle-meta">
                <span class="index">{{ item.index }}</span>
                <span class="time-range">
                  <template v-if="readonly">{{ item.startTime }} - {{ item.endTime }}</template>
                  <template v-else>
                    <el-input v-model="item.startTime" disabled size="mini" class="time-input" @blur="() => syncTimeFromString(item, 'start')" />
                    -
                    <el-input v-model="item.endTime" disabled size="mini" class="time-input" @blur="() => syncTimeFromString(item, 'end')" />
                  </template>
                </span>
                <span :class="['duration', { 'duration-warn': !readonly && getItemErrors(item).some(e => e.type === 'charsPerSecond') }]">
                  {{ formatDuration(item.endSeconds - item.startSeconds) }}
                </span>
                <div v-if="!readonly" class="item-actions" @click.stop>
                  <!-- <el-button type="text" size="mini" @click="addAbove(i)">上方添加</el-button>
                  <el-button type="text" size="mini" @click="addBelow(i)">下方添加</el-button>
                  <el-button type="text" size="mini" class="btn-delete" @click="deleteItem(i)">删除</el-button> -->
                </div>
              </div>
              <div class="subtitle-content">
                <div v-if="item.text" class="content-row">
                  <span class="content-label">{{ $t('subtitle.source') }}</span>
                  <div class="original-text">{{ item.text }}</div>
                </div>
                <div class="content-row">
                  <span class="content-label">{{ $t('subtitle.target') }}</span>
                  <div class="content-body">
                    <el-input
                      v-if="!readonly"
                      v-model="item.translatedText"
                      type="textarea"
                      :autosize="{ minRows: 1, maxRows: 4 }"
                      :placeholder="$t('subtitle.placeholder')"
                      @input="onItemChange"
                    />
                    <div v-else class="translated-text-readonly">{{ item.translatedText || '—' }}</div>
                    <span v-if="!readonly" class="row-info">{{ $tc('subtitle.charsCount', (item.translatedText || '').length, { count: (item.translatedText || '').length }) }}</span>
                  </div>
                </div>
              </div>
              <div v-if="!readonly && getItemErrors(item).length" class="item-errors">
                <span v-for="err in getItemErrors(item)" :key="err.type" :class="'err-' + err.type">{{ getErrorMessage(err) }}</span>
              </div>
            </div>
          </div>
          <div v-if="currentIndex >= 0" class="current-time-line" :style="currentTimeLineStyle" />
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import {
  validateSrtList,
  getDefaultMaxCharsPerSecond,
  srtTimeToSeconds,
  secondsToSrtTime
} from "@/utils/videoSrtValidation";

export default {
  name: "VideoSrtEditor",
  props: {
    /** 字幕列表，与上一系统一致：{ index, startSeconds, endSeconds, startTime, endTime, text, translatedText? }[] */
    srtList: {
      type: Array,
      default: () => []
    },
    /** 视频相对路径，如 sourcebook/test/0001.mp4 */
    videoPath: {
      type: String,
      default: ""
    },
    /** 视频域名或路径前缀，可选 */
    videoBaseUrl: {
      type: String,
      default: ""
    },
    /** 每秒最大字数限制 */
    maxCharsPerSecond: {
      type: Number,
      default: null
    },
    /** 只读模式：仅展示，不可编辑（如审核页） */
    readonly: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      initialized: false,
      list: [],
      currentTime: 0,
      currentIndex: -1,
      validationErrors: [],
      internalMaxCharsPerSecond: getDefaultMaxCharsPerSecond(),
      saveToParentTimer: null,
      nextId: 0
    };
  },
  computed: {
    videoSrc() {
      const path = this.videoPath || (this.list.length ? "" : "");
      if (!path) return "";
      const base = this.videoBaseUrl || process.env.VUE_APP_VIDEO_BASE || "";
      return base ? base.replace(/\/?$/, "/") + path.replace(/^\//, "") : path;
    },
    currentCueText() {
      if (this.currentIndex < 0 || !this.list[this.currentIndex]) return "";
      const item = this.list[this.currentIndex];
      return (item.translatedText != null && item.translatedText !== "") ? item.translatedText : item.text;
    },
    currentTimeLineStyle() {
      return {};
    }
  },
  watch: {
    srtList: {
      immediate: true,
      handler(val) {
        if (!Array.isArray(val)) return;
        this.list = val.map((item, i) => ({
          ...item,
          id: item.id != null ? item.id : "item-" + this.nextId++,
          translatedText: item.translatedText != null ? item.translatedText : ""
        }));
        this.internalMaxCharsPerSecond = this.maxCharsPerSecond != null
          ? Number(this.maxCharsPerSecond)
          : getDefaultMaxCharsPerSecond();
        this.initialized = true;
        if (!this.readonly) {
          this.$nextTick(() => {
            this.runValidation();
          });
        }
      }
    }
  },
  mounted() {
    document.addEventListener("keydown", this.handleKeydown);
  },
  beforeDestroy() {
    if (this.saveToParentTimer) clearTimeout(this.saveToParentTimer);
    document.removeEventListener("keydown", this.handleKeydown);
  },
  methods: {
    formatDuration(seconds) {
      const unit = this.$t("subtitle.durationUnit");
      const n = Number(seconds);
      if (!isFinite(n)) return "0" + unit;
      if (Math.abs(n - Math.round(n)) < 1e-12) {
        return Math.round(n) + unit;
      }
      return String(parseFloat(n.toFixed(12))) + unit;
    },
    runValidation() {
      const listForValidation = this.list.map(item => ({
        ...item,
        text: item.translatedText != null ? item.translatedText : ""
      }));
      this.validationErrors = validateSrtList(listForValidation, this.internalMaxCharsPerSecond);
    },
    getItemErrors(item) {
      return this.validationErrors.filter(e => e.index === item.index);
    },
    getErrorMessage(err) {
      return err.messageKey
        ? this.$t(err.messageKey, err.messageParams)
        : (err.message || "");
    },
    onItemChange() {
      this.runValidation();
      this.debouncedSaveToParent();
    },
    debouncedSaveToParent() {
      if (this.saveToParentTimer) clearTimeout(this.saveToParentTimer);
      this.saveToParentTimer = setTimeout(() => {
        this.saveToParentTimer = null;
        this.pushSaveToParent();
      }, 400);
    },
    createEmptySubtitle(startSeconds, endSeconds) {
      const start = Math.max(0, startSeconds);
      let end = Number(endSeconds);
      if (isNaN(end) || end <= start) {
        end = start + 1;
      }
      return {
        id: "new-" + this.nextId++,
        index: 0,
        startSeconds: start,
        endSeconds: end,
        startTime: secondsToSrtTime(start),
        endTime: secondsToSrtTime(end),
        text: "",
        translatedText: ""
      };
    },
    reindexList() {
      this.list.forEach((item, i) => {
        item.index = i + 1;
      });
    },
    addAbove(i) {
      const nextCue = this.list[i];
      const prev = i > 0 ? this.list[i - 1] : null;
      const start = prev ? prev.endSeconds : 0;
      const end = nextCue ? nextCue.startSeconds : start + 1;
      const newItem = this.createEmptySubtitle(start, end);
      this.list.splice(i, 0, newItem);
      this.reindexList();
      this.runValidation();
      this.debouncedSaveToParent();
      this.$message.success("已在上方添加一条字幕");
      this.$nextTick(() => {
        this.onSubtitleClick(this.list[i], i);
      });
    },
    addBelow(i) {
      const cur = this.list[i];
      const start = cur.endSeconds;
      const nextCue = this.list[i + 1];
      const end = nextCue ? nextCue.startSeconds : start + 1;
      const newItem = this.createEmptySubtitle(start, end);
      const newIndex = i + 1;
      this.list.splice(newIndex, 0, newItem);
      this.reindexList();
      this.runValidation();
      this.debouncedSaveToParent();
      this.$message.success("已在下方添加一条字幕");
      this.$nextTick(() => {
        this.onSubtitleClick(this.list[newIndex], newIndex);
      });
    },
    appendFirstSubtitle() {
      if (this.list.length === 0) {
        const newItem = this.createEmptySubtitle(0, 1);
        this.list.push(newItem);
        this.reindexList();
      } else {
        this.addBelow(this.list.length - 1);
        return;
      }
      this.runValidation();
      this.debouncedSaveToParent();
      this.$message.success("已添加一条字幕");
      this.$nextTick(() => {
        this.onSubtitleClick(this.list[0], 0);
      });
    },
    deleteItem(i) {
      if (this.list.length <= 1) {
        this.$message.warning("至少保留一条字幕");
        return;
      }
      this.list.splice(i, 1);
      this.reindexList();
      if (this.currentIndex >= this.list.length) this.currentIndex = this.list.length - 1;
      else if (this.currentIndex >= i && this.currentIndex > 0) this.currentIndex--;
      this.runValidation();
      this.debouncedSaveToParent();
      this.$message.success("已删除");
    },
    pushSaveToParent() {
      const payload = {
        srcSrtContent: this.list.map(item => ({
          index: item.index,
          startSeconds: item.startSeconds,
          endSeconds: item.endSeconds,
          startTime: item.startTime,
          endTime: item.endTime,
          text: item.text != null ? item.text : "",
          translatedText: item.translatedText != null ? item.translatedText : ""
        })),
        valid: this.validationErrors.filter(e => e.type !== 'charsPerSecond').length === 0,
        validationErrors: this.validationErrors.length ? this.validationErrors : undefined,
        summary: this.validationErrors.length ? this.$tc("subtitle.validationIssueSummary", this.validationErrors.length, { count: this.validationErrors.length }) : undefined
      };
      this.$emit("change", payload);
    },
    syncTimeFromString(item, which) {
      if (which === "start") {
        const sec = srtTimeToSeconds(item.startTime);
        if (!isNaN(sec)) {
          item.startSeconds = sec;
          item.startTime = secondsToSrtTime(sec);
        }
      } else {
        const sec = srtTimeToSeconds(item.endTime);
        if (!isNaN(sec)) {
          item.endSeconds = sec;
          item.endTime = secondsToSrtTime(sec);
        }
      }
      this.runValidation();
      this.debouncedSaveToParent();
    },
    /** 仅在字幕列表容器内滚动，不触发外层页面滚动 */
    scrollItemIntoViewInContainer(container, el) {
      const containerHeight = container.clientHeight;
      const itemTop = el.offsetTop;
      const itemHeight = el.offsetHeight;
      let targetScrollTop = container.scrollTop;
      if (itemTop < container.scrollTop) {
        targetScrollTop = itemTop;
      } else if (itemTop + itemHeight > container.scrollTop + containerHeight) {
        targetScrollTop = itemTop + itemHeight - containerHeight;
      }
      if (targetScrollTop !== container.scrollTop) {
        container.scrollTo({ top: targetScrollTop, behavior: "smooth" });
      }
    },
    onSubtitleClick(item, i) {
      this.currentIndex = i;
      const video = this.$refs.videoEl;
      if (video) {
        video.currentTime = Math.round(item.startSeconds * 1000) / 1000;
      }
      const container = this.$refs.subtitleListEl;
      const el = container && container.querySelectorAll(".subtitle-item")[i];
      if (container && el) this.scrollItemIntoViewInContainer(container, el);
    },
    onVideoTimeUpdate() {
      const video = this.$refs.videoEl;
      if (!video) return;
      // 按毫秒精度舍入，与 SRT 00:00:04,087 一致，避免浮点误差导致匹配错位
      const t = Math.round(video.currentTime * 1000) / 1000;
      this.currentTime = t;
      let idx = -1;
      // 使用 [start, end) 左闭右开，避免相邻字幕首尾时间相同时两条都匹配，导致定位到上一条
      this.list.forEach((item, i) => {
        if (t >= item.startSeconds && t < item.endSeconds) idx = i;
      });
      this.currentIndex = idx;
      if (idx >= 0 && this.$refs.subtitleListEl) {
        const container = this.$refs.subtitleListEl;
        const el = container.querySelectorAll(".subtitle-item")[idx];
        if (el) this.scrollItemIntoViewInContainer(container, el);
      }
    },
    handleSave() {
      this.runValidation();
      const blockingErrors = this.validationErrors.filter(e => e.type !== 'charsPerSecond');
      if (blockingErrors.length > 0) {
        this.$message.warning("存在校验问题，请修正后再保存");
        return;
      }
      const payload = {
        srcSrtContent: this.list.map(item => ({
          index: item.index,
          startSeconds: item.startSeconds,
          endSeconds: item.endSeconds,
          startTime: item.startTime,
          endTime: item.endTime,
          text: item.text != null ? item.text : "",
          translatedText: item.translatedText != null ? item.translatedText : ""
        }))
      };
      this.$emit("save", payload);
      this.$message.success("已提交保存");
    },
    handleKeydown(e) {
      if (!this.initialized) return;
      const video = this.$refs.videoEl;
      const tag = e.target.tagName.toLowerCase();
      if (tag === "input" || tag === "textarea" || e.target.isContentEditable) return;
      if (e.code === "Space") {
        e.preventDefault();
        if (video) {
          if (video.paused) video.play().catch(() => {});
          else video.pause();
        }
        return;
      }
      if (e.code === "ArrowDown" && this.list.length) {
        e.preventDefault();
        const next = Math.min(this.currentIndex + 1, this.list.length - 1);
        this.onSubtitleClick(this.list[next], next);
        return;
      }
      if (e.code === "ArrowUp" && this.list.length) {
        e.preventDefault();
        const prev = Math.max(this.currentIndex - 1, 0);
        this.onSubtitleClick(this.list[prev], prev);
        return;
      }
    }
  }
};
</script>

<style lang="less" scoped>
.video-srt-editor {
  height: 100%;
  overflow: hidden;
  position: relative;
}
.editor-waiting {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  color: #909399;
  p { margin-top: 12px; }
}
.editor-layout {
  display: flex;
  height: 100%;
}
.video-section {
  width: 40%;
  min-width: 320px;
  display: flex;
  flex-direction: column;
  background: #f5f5f5;
  position: relative;
  .video-player {
    width: 100%;
    flex: 1;
    min-height: 300px;
    object-fit: contain;
  }
  .subtitle-preview {
    position: absolute;
    bottom: 80px;
    left: 0;
    right: 0;
    text-align: center;
    color: #fff;
    font-size: 16px;
  }
}
.list-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  background: #fff;
  border-left: 1px solid #ebeef5;
  overflow: hidden;
}
.list-toolbar {
  padding: 8px 12px;
  border-bottom: 1px solid #ebeef5;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
  .error-summary { color: #e6a23c; font-size: 12px; }
  .toolbar-actions { display: flex; align-items: center; gap: 8px; }
}
.subtitle-list {
  flex: 1;
  overflow-y: auto;
  padding: 8px;
  position: relative;
}
.subtitle-list .current-time-line {
  top: 8px;
}
.time-input {
  width: 120px;
  display: inline-block;
}
.time-input .el-input__inner { font-family: monospace; }
.subtitle-item {
  padding: 10px 12px;
  margin-bottom: 8px;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  cursor: pointer;
  transition: background 0.2s;
  &.active {
    background: #ecf5ff;
    border-color: #409eff;
    border-left: 3px solid #f56c6c;
  }
  &.error {
    border-color: #f56c6c;
    background: #fef0f0;
  }
  .subtitle-meta {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 6px;
    font-size: 12px;
    color: #606266;
    .index { font-weight: 600; }
    .time-range { flex: 1; min-width: 0; }
    .duration-warn { color: #e6a23c; }
    .item-actions {
      display: flex;
      align-items: center;
      gap: 4px;
      margin-left: auto;
      .el-button { padding: 0 4px; font-size: 12px; }
      .btn-delete { color: #f56c6c; }
      .btn-delete:hover { color: #f78989; }
    }
  }
  .subtitle-content {
    .content-row {
      display: flex;
      align-items: flex-start;
      gap: 8px;
      margin-bottom: 6px;
      &:last-child {
        margin-bottom: 0;
      }
    }
    .content-label {
      flex-shrink: 0;
      padding: 4px 6px;
      font-size: 12px;
      color: #909399;
      line-height: 22px;
    }
    .content-body {
      flex: 1;
      min-width: 0;
    }
    .original-text {
      flex: 1;
      min-width: 0;
      padding: 6px 10px;
      background: #f5f7fa;
      border-radius: 4px;
      font-size: 13px;
      color: #606266;
      line-height: 1.5;
      white-space: pre-wrap;
      word-break: break-word;
    }
    .translated-text-readonly {
      padding: 6px 10px;
      min-height: 32px;
      background: #fafafa;
      border-radius: 4px;
      font-size: 13px;
      color: #303133;
      line-height: 1.5;
      white-space: pre-wrap;
      word-break: break-word;
    }
    .row-info { font-size: 12px; color: #909399; }
  }
  .item-errors {
    margin-top: 6px;
    font-size: 12px;
    .err-charsPerSecond { color: #e6a23c; }
    .err-timeOrder { color: #f56c6c; }
  }
}
.list-section { position: relative; }
</style>
